import {
  AuthContainer,
  AuthHeader,
  AuthErrorBanner,
  AuthPasswordField,
  AuthSubmitButton,
} from '../atoms';
import type { FormEvent } from 'react';
import type { AuthConfig } from '../../types';
import { theme } from '../../styles/theme';
import {
  VerifyStatusContainer,
  VerifyStatusSuccessContent,
  VerifyStatusSuccessIcon,
  VerifyStatusSuccessIconSvg,
  VerifyStatusTextCenter,
} from './styled';

export interface ResetPasswordFormProps {
  newPassword: string;
  confirmPassword: string;
  onNewPasswordChange: (password: string) => void;
  onConfirmPasswordChange: (password: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  error?: string;
  loading?: boolean;
  success?: boolean;
  authConfig: AuthConfig;
  title?: string;
  subtitle?: string;
  newPasswordLabel?: string;
  newPasswordPlaceholder?: string;
  confirmPasswordLabel?: string;
  confirmPasswordPlaceholder?: string;
  submitButtonText?: string;
  loadingButtonText?: string;
  successTitle?: string;
}

/**
 * Pre-built reset password form component (UI only, no business logic).
 */
export function ResetPasswordForm({
  newPassword,
  confirmPassword,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  error,
  loading = false,
  success = false,
  authConfig,
  title = 'Reset Password',
  subtitle = 'Enter your new password below.',
  newPasswordLabel = 'New Password',
  newPasswordPlaceholder = '••••••',
  confirmPasswordLabel = 'Confirm Password',
  confirmPasswordPlaceholder = '••••••',
  submitButtonText = 'Reset Password',
  loadingButtonText = 'Resetting...',
  successTitle = 'Password Reset Successful!',
}: ResetPasswordFormProps) {
  let successMessage =
    'Your password has been successfully reset. You can close this page and sign in with your new password.';
  if (authConfig && authConfig.verifyEmailMethod === 'code') {
    successMessage =
      'Your password has been successfully reset. You can wait for redirect to sign in with your new password.';
  }

  // Success state
  if (success) {
    return (
      <AuthContainer>
        <VerifyStatusContainer $stretch>
          <VerifyStatusSuccessContent>
            <VerifyStatusSuccessIcon>
              <VerifyStatusSuccessIconSvg>
                <svg
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </VerifyStatusSuccessIconSvg>
            </VerifyStatusSuccessIcon>
            <VerifyStatusTextCenter style={{ fontSize: '1.5rem', fontWeight: 600 }}>
              {successTitle}
            </VerifyStatusTextCenter>
            <VerifyStatusTextCenter style={{ color: theme.colors.textSecondary }}>
              {successMessage}
            </VerifyStatusTextCenter>
          </VerifyStatusSuccessContent>
        </VerifyStatusContainer>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer>
      <AuthHeader title={title} subtitle={subtitle} />

      <AuthErrorBanner error={error || ''} />

      <form
        onSubmit={onSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[6] }}
      >
        <AuthPasswordField
          id="newPassword"
          label={newPasswordLabel}
          placeholder={newPasswordPlaceholder}
          value={newPassword}
          onChange={(e) => onNewPasswordChange(e.target.value)}
          required
          autoComplete="new-password"
          showStrengthIndicator
          authConfig={authConfig}
        />

        <AuthPasswordField
          id="confirmPassword"
          label={confirmPasswordLabel}
          placeholder={confirmPasswordPlaceholder}
          value={confirmPassword}
          onChange={(e) => onConfirmPasswordChange(e.target.value)}
          required
          autoComplete="new-password"
          authConfig={authConfig}
        />

        <AuthSubmitButton isLoading={loading} disabled={loading}>
          {loading ? loadingButtonText : submitButtonText}
        </AuthSubmitButton>
      </form>
    </AuthContainer>
  );
}
